import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, orderBy, getDocs, addDoc, updateDoc, deleteDoc,
  doc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { useAuth } from '../../../auth/components/AuthProvider';

const TIPOS = [
  { id: 'urgente',     label: 'Urgente',     emoji: '🚨', color: '#ef4444', bg: 'rgba(239,68,68,0.1)',   border: 'rgba(239,68,68,0.3)'   },
  { id: 'precaucion',  label: 'Precaución',  emoji: '⚠️', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)',  border: 'rgba(245,158,11,0.3)'  },
  { id: 'informacion', label: 'Información', emoji: 'ℹ️', color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.3)'  },
];

const TEMPORADAS = [
  { id: 'invierno',  label: 'Invierno',    meses: 'Jun · Jul · Ago', emoji: '❄️',  color: '#60a5fa', bg: 'rgba(96,165,250,0.1)',  border: 'rgba(96,165,250,0.25)'  },
  { id: 'primavera', label: 'Primavera',   meses: 'Sep · Oct · Nov', emoji: '🌸',  color: '#34d399', bg: 'rgba(52,211,153,0.1)',  border: 'rgba(52,211,153,0.25)'  },
  { id: 'verano',    label: 'Verano',      meses: 'Dic · Ene · Feb', emoji: '☀️',  color: '#fbbf24', bg: 'rgba(251,191,36,0.1)',  border: 'rgba(251,191,36,0.25)'  },
  { id: 'otono',     label: 'Otoño',       meses: 'Mar · Abr · May', emoji: '🍂',  color: '#f97316', bg: 'rgba(249,115,22,0.1)',  border: 'rgba(249,115,22,0.25)'  },
  { id: 'todo',      label: 'Todo el año', meses: 'Siempre visible', emoji: '📅',  color: '#a78bfa', bg: 'rgba(167,139,250,0.1)', border: 'rgba(167,139,250,0.25)' },
];

const MES_A_TEMPORADA = {
  0: 'verano', 1: 'verano', 11: 'verano',
  2: 'otono',  3: 'otono',  4: 'otono',
  5: 'invierno', 6: 'invierno', 7: 'invierno',
  8: 'primavera', 9: 'primavera', 10: 'primavera',
};

const EMOJIS = ['🚨','⚠️','ℹ️','❄️','☀️','🌸','🍂','🌧️','💨','🔥','❗','🛑','🔔','📣','⛔','🌊','🌪️'];
const FORM_EMPTY = { titulo: '', mensaje: '', tipo: 'precaucion', temporada: 'invierno', icono: '⚠️', activo: true, orden: 0 };

function fmtDate(val) {
  if (!val) return '—';
  const d = val?.toDate?.() ?? new Date(val);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getTemporadaActual() {
  return MES_A_TEMPORADA[new Date().getMonth()] || 'todo';
}

function TipoBadge({ id }) {
  const t = TIPOS.find(x => x.id === id);
  if (!t) return null;
  return (
    <span style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold">
      {t.emoji} {t.label}
    </span>
  );
}

function TemporadaBadge({ id }) {
  const t = TEMPORADAS.find(x => x.id === id);
  if (!t) return null;
  return (
    <span style={{ background: t.bg, border: `1px solid ${t.border}`, color: t.color }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold">
      {t.emoji} {t.label}
    </span>
  );
}

/* ── Modal crear / editar ─────────────────────────────────────────── */
function ModalAlerta({ alerta, onClose, onSaved, adminUid }) {
  const isNew = !alerta?.id;
  const [form, setForm] = useState(isNew ? { ...FORM_EMPTY } : {
    titulo:    alerta.titulo    || '',
    mensaje:   alerta.mensaje   || '',
    tipo:      alerta.tipo      || 'precaucion',
    temporada: alerta.temporada || 'invierno',
    icono:     alerta.icono     || '⚠️',
    activo:    alerta.activo    ?? true,
    orden:     alerta.orden     ?? 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const tipo = TIPOS.find(t => t.id === form.tipo) || TIPOS[1];

  const handleSave = async () => {
    if (!form.titulo.trim())  { setError('El título es obligatorio.'); return; }
    if (!form.mensaje.trim()) { setError('El mensaje es obligatorio.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        titulo:       form.titulo.trim(),
        mensaje:      form.mensaje.trim(),
        tipo:         form.tipo,
        temporada:    form.temporada,
        icono:        form.icono.trim() || '⚠️',
        activo:       form.activo,
        orden:        Number(form.orden) || 0,
        actualizadoEn: serverTimestamp(),
      };
      if (isNew) {
        payload.creadoEn = serverTimestamp();
        if (adminUid) payload.creadoPor = adminUid;
        await addDoc(collection(db, 'alertas_temporada'), payload);
      } else {
        await updateDoc(doc(db, 'alertas_temporada', alerta.id), payload);
      }
      onSaved(); onClose();
    } catch (e) { setError('Error al guardar: ' + e.message); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-black/60" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h2 className="text-base font-black text-gray-900">
              {isNew ? 'Nueva alerta de temporada' : 'Editar alerta'}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
          </div>
          <div className="p-6 space-y-4">

            {/* Tipo */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Tipo de alerta</label>
              <div className="flex flex-wrap gap-2">
                {TIPOS.map(t => (
                  <button key={t.id} type="button"
                    onClick={() => { set('tipo', t.id); set('icono', t.emoji); }}
                    style={form.tipo === t.id ? { background: t.bg, borderColor: t.border, color: t.color } : {}}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${
                      form.tipo === t.id ? '' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}>
                    {t.emoji} {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Temporada */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Temporada</label>
              <div className="flex flex-wrap gap-2">
                {TEMPORADAS.map(t => (
                  <button key={t.id} type="button" onClick={() => set('temporada', t.id)}
                    style={form.temporada === t.id ? { background: t.bg, borderColor: t.border, color: t.color } : {}}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-bold transition-all ${
                      form.temporada === t.id ? '' : 'border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}>
                    {t.emoji} {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Ícono */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Ícono</label>
              <div className="flex gap-2 items-center">
                <input type="text" value={form.icono} onChange={e => set('icono', e.target.value)} maxLength={8}
                  className="w-16 text-center text-2xl border border-gray-300 rounded-xl px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-300" />
                <div className="flex flex-wrap gap-1.5">
                  {EMOJIS.map(e => (
                    <button key={e} type="button" onClick={() => set('icono', e)}
                      className={`text-lg p-1 rounded-lg ${form.icono === e ? 'bg-amber-100 ring-2 ring-amber-300' : 'hover:bg-gray-100'}`}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Título */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Título</label>
              <input type="text" value={form.titulo} onChange={e => set('titulo', e.target.value)} maxLength={100}
                placeholder="Ej: Alerta de heladas en zonas cordilleranas"
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
            </div>

            {/* Mensaje */}
            <div>
              <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">
                Mensaje <span className="font-normal normal-case text-gray-300 ml-1">{form.mensaje.length}/200</span>
              </label>
              <textarea value={form.mensaje} onChange={e => set('mensaje', e.target.value)} rows={3} maxLength={200}
                placeholder="Descripción visible en la app..."
                className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-300" />
            </div>

            {/* Orden y activo */}
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Orden (menor = primero)</label>
                <input type="number" min={0} max={999} value={form.orden} onChange={e => set('orden', e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
              </div>
              <div className="pb-0.5">
                <label className="block text-[10px] font-black uppercase tracking-wider text-gray-400 mb-2">Visible en app</label>
                <button type="button" onClick={() => set('activo', !form.activo)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl border font-bold text-xs transition-all ${
                    form.activo ? 'bg-green-50 border-green-200 text-green-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                  <span className={`w-3 h-3 rounded-full ${form.activo ? 'bg-green-500' : 'bg-gray-300'}`} />
                  {form.activo ? 'Activa' : 'Inactiva'}
                </button>
              </div>
            </div>

            {error && <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">{error}</div>}

            <div className="flex gap-3 pt-1">
              <button onClick={onClose} className="flex-1 border border-gray-300 rounded-xl py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancelar</button>
              <button onClick={handleSave} disabled={saving}
                style={{ background: tipo.color }}
                className="flex-1 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-50 hover:opacity-90">
                {saving ? 'Guardando…' : isNew ? 'Crear alerta' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Componente principal ─────────────────────────────────────────── */
export default function AdminAlertasTemporada() {
  const { user } = useAuth();
  const [alertas, setAlertas]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [filtroTipo, setFiltroTipo]   = useState('');
  const [filtroTemp, setFiltroTemp]   = useState('');
  const [modalEditar, setModalEditar] = useState(null);
  const [eliminando, setEliminando]   = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'alertas_temporada'), orderBy('temporada'), orderBy('orden'))
      );
      setAlertas(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch {
      try {
        const snap = await getDocs(collection(db, 'alertas_temporada'));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          if (a.temporada !== b.temporada) return (a.temporada || '').localeCompare(b.temporada || '');
          return (a.orden || 0) - (b.orden || 0);
        });
        setAlertas(list);
      } catch (e) { console.error(e); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleToggle = async (a) => {
    await updateDoc(doc(db, 'alertas_temporada', a.id), { activo: !a.activo, actualizadoEn: serverTimestamp() });
    setAlertas(prev => prev.map(x => x.id === a.id ? { ...x, activo: !x.activo } : x));
  };

  const handleEliminar = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.titulo}"?`)) return;
    setEliminando(a.id);
    try { await deleteDoc(doc(db, 'alertas_temporada', a.id)); setAlertas(p => p.filter(x => x.id !== a.id)); }
    catch (e) { alert('Error: ' + e.message); }
    finally { setEliminando(null); }
  };

  const temporadaActual = getTemporadaActual();

  const filtradas = alertas.filter(a => {
    if (filtroTipo && a.tipo !== filtroTipo) return false;
    if (filtroTemp && a.temporada !== filtroTemp) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="flex flex-wrap gap-3">
        {[
          { label: 'Total',    count: alertas.length,                    color: 'text-gray-700',  bg: 'bg-white border-gray-200' },
          { label: 'Activas',  count: alertas.filter(a => a.activo).length,  color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
          { label: 'Inactivas',count: alertas.filter(a => !a.activo).length, color: 'text-gray-400',  bg: 'bg-gray-50 border-gray-200' },
        ].map(s => (
          <div key={s.label} className={`rounded-xl border px-4 py-3 ${s.bg}`}>
            <div className={`text-2xl font-black ${s.color}`}>{s.count}</div>
            <div className="text-xs font-semibold text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
        <div className="rounded-xl border px-4 py-3 bg-amber-50 border-amber-200">
          <div className="text-2xl font-black text-amber-700">
            {TEMPORADAS.find(t => t.id === temporadaActual)?.emoji}
          </div>
          <div className="text-xs font-semibold text-gray-500 mt-0.5">
            {TEMPORADAS.find(t => t.id === temporadaActual)?.label}
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap gap-2 items-center">
          <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300">
            <option value="">Todos los tipos</option>
            {TIPOS.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.label}</option>)}
          </select>
          <select value={filtroTemp} onChange={e => setFiltroTemp(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-300">
            <option value="">Todas las temporadas</option>
            {TEMPORADAS.map(t => <option key={t.id} value={t.id}>{t.emoji} {t.label}</option>)}
          </select>
          <span className="text-xs text-gray-400">{filtradas.length} alertas</span>
        </div>
        <div className="flex gap-2">
          <button onClick={cargar} className="text-xs text-gray-500 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50">Actualizar</button>
          <button onClick={() => setModalEditar({})}
            className="bg-amber-600 text-white px-4 py-1.5 rounded-xl text-sm font-bold hover:bg-amber-700">
            + Nueva alerta
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center text-gray-400">Cargando…</div>
      ) : filtradas.length === 0 ? (
        <div className="py-12 text-center space-y-2">
          <p className="text-4xl">⚠️</p>
          <p className="text-gray-500">No hay alertas de temporada.</p>
          <button onClick={() => setModalEditar({})} className="text-amber-600 font-semibold text-sm hover:underline">Crear la primera →</button>
        </div>
      ) : (
        <div className="space-y-2">
          {filtradas.map(a => {
            const tipo = TIPOS.find(t => t.id === a.tipo) || TIPOS[1];
            const temp = TEMPORADAS.find(t => t.id === a.temporada);
            const esActual = a.temporada === temporadaActual || a.temporada === 'todo';
            return (
              <div key={a.id}
                className={`bg-white rounded-2xl border overflow-hidden transition-shadow hover:shadow-sm ${!a.activo ? 'opacity-60' : ''}`}
                style={{ borderLeftWidth: 3, borderLeftColor: tipo.color }}>
                <div className="px-4 py-3 flex items-start gap-3">
                  <span className="text-2xl flex-shrink-0 mt-0.5">{a.icono || tipo.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <TipoBadge id={a.tipo} />
                      {temp && <TemporadaBadge id={a.temporada} />}
                      {esActual && a.activo && (
                        <span className="text-[9px] font-black uppercase text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full">
                          Activa ahora
                        </span>
                      )}
                      {!a.activo && <span className="text-[10px] font-bold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">Inactiva</span>}
                    </div>
                    <p className="font-bold text-gray-900 text-sm leading-tight">{a.titulo}</p>
                    {a.mensaje && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{a.mensaje}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">{fmtDate(a.creadoEn)}</span>
                    <button onClick={() => handleToggle(a)}
                      className={`w-8 h-4 rounded-full relative transition-colors ${a.activo ? 'bg-green-500' : 'bg-gray-300'}`}>
                      <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all ${a.activo ? 'left-4' : 'left-0.5'}`} />
                    </button>
                    <button onClick={() => setModalEditar(a)} className="text-xs font-semibold text-indigo-600 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 rounded-lg px-2.5 py-1">Editar</button>
                    <button onClick={() => handleEliminar(a)} disabled={eliminando === a.id}
                      className="text-xs font-semibold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 rounded-lg px-2.5 py-1 disabled:opacity-40">
                      {eliminando === a.id ? '…' : '✕'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modalEditar !== null && (
        <ModalAlerta
          alerta={modalEditar?.id ? modalEditar : null}
          onClose={() => setModalEditar(null)}
          onSaved={cargar}
          adminUid={user?.uid}
        />
      )}
    </div>
  );
}
